import { Line } from "react-chartjs-2"
import { Chart, ChartEvent, registerables, TooltipItem, TooltipModel } from "chart.js"
import classNames from "classnames"
import dayjs from "dayjs"

import { ApiTypes } from "types"

import getLegendMarginPlugin from "utils/chartjs/getLegendMarginPlugin"
import { borderColorWithSkipped, borderDashWithSkipped, defaultChartTooltipOptions } from "utils/chartUtils"
import { useWindowResize } from "hooks/useWindowResize"
import { NoDataLayer } from "components/chart/NoDataLayer"

import { formatMonitoringXAxisTick, XAxisTickFormat } from "./formatMonitoringXAxisTick"
import { getMaxDivider } from "./getMaxDivider"

import styles from "./index.module.scss"

Chart.register(...registerables)

interface Props {
  settings: {
    ticksCount: number
    dataCountsDivider: number
    xAxisTickFormat: XAxisTickFormat
  }
  historicalData: ApiTypes.Res.MonitoringHistoricalDataBucket[]
  isSingleDataPoint: boolean
  showSetHumidity: boolean
  timeZone: string
}

const HumidityChart = ({ settings, historicalData, isSingleDataPoint, showSetHumidity, timeZone }: Props) => {
  const isLargeScreen = useWindowResize()
  const parsedData =
    historicalData.map(item => ({
      humidity: item["avg#average_current_humidity_embedded"].value,
      setHumidity: item["avg#average_set_humidity"].value,
      timestamp: item.key_as_string,
    })) || []
  const noData = parsedData.length === 0

  const commonDatasetValues = {
    fill: "start",
    backgroundColor: "transparent",
    pointStyle: isSingleDataPoint as unknown as "circle",
    spanGaps: true,
    segment: {
      borderDash: borderDashWithSkipped,
      borderColor: borderColorWithSkipped,
    },
    borderWidth: 2,
  }

  const data = {
    labels: [...parsedData.map(item => item.timestamp)],
    datasets: [
      {
        label: "Current humidity",
        ...commonDatasetValues,
        data: parsedData.map(item => item.humidity),
        borderColor: "#6266f1",
        pointBackgroundColor: "#6266f1",
      },
      ...(showSetHumidity
        ? [
            {
              label: "Set humidity",
              ...commonDatasetValues,
              data: parsedData.map(item => item.setHumidity),
              borderColor: "#f6ad55",
              pointBackgroundColor: "#f6ad55",
            },
          ]
        : []),
      ...(parsedData.length === 0
        ? [
            {
              label: "",
              data: [50, 51, 52, 53, 54],
            },
          ]
        : []),
    ],
  }

  const shouldRotateXAxisLabels =
    (settings.xAxisTickFormat === "time" && settings.ticksCount >= 24) ||
    (settings.xAxisTickFormat === "date" && settings.ticksCount >= 30)
  const xAxisLabelRotation = shouldRotateXAxisLabels ? 45 : 0

  const options = {
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
    },
    plugins: {
      legend: {
        display: !noData,
        align: "end" as const,
        labels: {
          color: "#94A3B8",
          usePointStyle: true,
          pointStyle: "rect",
        },
      },
      tooltip: {
        ...defaultChartTooltipOptions,
        displayColors: true,
        callbacks: {
          title: function (this: TooltipModel<"line">, tooltipItem: TooltipItem<"line">[]) {
            const date = dayjs(tooltipItem[0].label).tz(timeZone || "UTC")
            return date.format("LLL")
          },
          label: function (this: TooltipModel<"line">, tooltipItem: TooltipItem<"line">) {
            return `${tooltipItem.formattedValue}%`
          },
        },
      },
    },
    scales: {
      x: {
        border: {
          color: "#e2e8f0",
        },
        grid: {
          color: "#e2e8f0",
        },
        ticks: {
          minRotation: xAxisLabelRotation,
          maxRotation: xAxisLabelRotation,
          maxTicksLimit: isLargeScreen ? settings.ticksCount : getMaxDivider(settings.ticksCount),
          color: "#94A3B8",
          callback: (value: number | string) => {
            const label = data.labels[Number(value)] ?? value
            return formatMonitoringXAxisTick(label, settings.xAxisTickFormat, timeZone)
          },
        },
      },
      y: {
        position: "left" as const,
        border: {
          display: false,
          color: "rgba(0,0,0,0)",
        },
        ticks: {
          maxTicksLimit: 6,
          stepSize: parsedData.length === 0 ? 1 : 0.1,
          color: "#94A3B8",
          callback: function (value: string | number) {
            return `${Number(value).toFixed(1)}%`
          },
        },
      },
    },
    onHover: (ctx: ChartEvent) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const chart: any = (ctx as any).chart
      chart.data.datasets[0].pointStyle = true
      if (showSetHumidity) {
        chart.data.datasets[1].pointStyle = true
      }
    },
  }

  return (
    <div
      className={classNames(styles.chartContainer, {
        [styles.chartContainerWithLabel]: !noData,
        [styles.emptyReportDataWrapper]: noData,
      })}
    >
      {noData && <NoDataLayer />}
      <Line data={data} options={options} plugins={[getLegendMarginPlugin(15)]} />
    </div>
  )
}

export default HumidityChart
